<template>
  <page-view :is-loading="isLoading">
    <template #header>
      <base-header
        :title="pageTitle"
        :subtitle="pageSubtitle"
        :has-intro="!isMobileBreakpoint"
        @title="handleBackToPlaylists"
        @back="handleBackToPlaylists"
      >
        <template #info>
          <template>
            <v-chip-legacy class="external-chip ma-0 mr-3" dense>
              <v-icon left> panda-help-outline </v-icon>
              {{ 'playlist' }}
            </v-chip-legacy>
          </template>
        </template>
        <template #actions>
          <div
            class="d-flex align-center"
            :class="{ 'mobile-actions': isMobileBreakpoint }"
          >
            <playlist-share-modal />
            <base-btn
              type="tertiary"
              @click="handleSharePlaylist"
              class="action-btn mr-2"
            >
              {{ $t('message.playlist.headerBtns.share') }}
            </base-btn>

            <base-btn @click="openAddVideoModal" class="action-btn mr-2">
              {{ $t('message.playlist.headerBtns.addVideo') }}
            </base-btn>

            <v-icon class="action-icon" @click="handleSharePlaylist"
              >mdi-open-in-new</v-icon
            >
          </div>
        </template>
      </base-header>
    </template>

    <template #content>
      <!-- content -->
      <div class="grid">
        <div v-if="!isMobileBreakpoint" class="grid__playlists">
          <v-container class="pa-0 f-height">
            <v-row no-gutters class="f-height">
              <v-col :cols="isMobileBreakpoint ? '12' : '12'" class="f-height">
                <v-card
                  class="px-4 py-6 neutral-100-white card__empty-state--head"
                  flat
                >
                  <p
                    class="body-regular-base font-weight-medium neutral-20-dark--text mb-1"
                  >
                    {{ playlistTitle }}
                  </p>
                  <p
                    class="body-regular-small font-weight-regular neutral-40-light--text mb-0"
                  >
                    {{ playlistDescription }}
                    <span
                      class="brand-60-base--text font-weight-medium ml-2"
                      @click="expandedDescription = true"
                      v-if="!expandedDescription"
                    >
                      Ver mais
                    </span>
                  </p>
                </v-card>
                <v-card
                  outlined
                  class="card__empty-state--content"
                  v-if="!hasAnyVideo"
                >
                  <img width="96" src="@/assets/panda_add_video.svg" />
                  <p class="body-regular-large neutral-20-dark--text my-6">
                    Nenhum vídeo foi adicionado
                  </p>
                  <base-btn @click="openAddVideoModal"
                    >Adicionar vídeo</base-btn
                  >
                </v-card>
                <template v-else>
                  <v-card outlined class="card__video-list">
                    <v-card
                      flat
                      class="video__item"
                      v-for="(video, index) in playlists_videos"
                      :key="index"
                    >
                      <div class="d-flex flex-column">
                        <p
                          class="body-regular-small font-weight-medium neutral-10-darker--text mb-1"
                          :title="
                            video.video ? video.video.title : video.video_id
                          "
                        >
                          {{ video.video ? video.video.title : video.video_id }}
                        </p>
                        <span
                          class="body-regular-smaller neutral-40-light--text mr-3"
                          >{{
                            video.video ? formatTime(video.video.duration) : '-'
                          }}</span
                        >
                      </div>
                      <div class="video__thumb">
                        <img
                          :src="video.video ? video.video.thumbnail_url : ''"
                          alt=""
                        />
                      </div>
                    </v-card>
                  </v-card>
                </template>
              </v-col>
            </v-row>
          </v-container>
        </div>

        <div
          class="grid__settings"
          :class="{ 'grid__settings--mobile': isMobileBreakpoint }"
        >
          <v-container class="pa-0">
            <v-row no-gutters>
              <v-col cols="12">
                <div class="body-bold-medium pl-2">
                  {{ $t('message.playlist.settings.title') }}
                </div>
                <playlist-tabs>
                  <template #general-tab>
                    <general-tab :playlist="playlist"></general-tab>
                  </template>
                  <template #videos-tab>
                    <videos-tab></videos-tab>
                  </template>
                  <template #advanced-tab>
                    <advanced-tab></advanced-tab>
                  </template>
                </playlist-tabs>
              </v-col>
            </v-row>
          </v-container>
        </div>

        <div v-if="!isMobileBreakpoint" class="grid__spacer spacer"></div>

        <div class="grid__footer d-flex flex-column actions-column">
          <div class="divider-div">
            <v-divider></v-divider>
          </div>
          <portal-target name="actions-footer" multiple></portal-target>
        </div>
      </div>
      <AddVideoPlaylist
        :playlistId="playlist.id"
        :dialogVisible="dialogVisible"
        @update:dialogVisible="dialogVisible = $event"
      />
    </template>
  </page-view>
</template>

<script>
// external imports
import { mapMutations, mapState } from 'vuex'

// internal imports
import i18n from '@/translations/lang'
import downloadUrlMixin from '@/utils/downloadUrl.mixin.js'
import formatTimeMixin from '@/utils/formatTime.mixins.js'

// API
import playlistApi from '@/services/playlistApi'

// Components
import PageView from '@/layout/PlaylistPage/PageView_PlaylistPage.vue'
import PlaylistTabs from './Settings/PlaylistTabs.vue'

import GeneralTab from './Settings/Tabs/GeneralTab.vue'
import VideosTab from './Settings/Tabs/VideosTab.vue'
import AdvancedTab from './Settings/Tabs/AdvancedTab.vue'
import PlaylistShareModal from './PlaylistShareModal.vue'
import AddVideoPlaylist from './Settings/Modals/AddVideo.vue'

export default {
  components: {
    PlaylistTabs,
    GeneralTab,
    VideosTab,
    AdvancedTab,
    PageView,
    BaseHeader: () =>
      import('../../layout/PlaylistPage/BaseHeader_PlaylistPage.vue'),
    BaseBtn: () => import('@/components/BaseBtn.vue'),
    PlaylistShareModal,
    AddVideoPlaylist,
  },
  mixins: [downloadUrlMixin, formatTimeMixin],
  name: 'Playlists',
  directives: {},
  data: () => ({
    isLoading: true,
    expandedDescription: false,
    dialogVisible: false,
    videosInfo: {},
  }),
  provide() {
    return {}
  },
  async created() {
    await this.getPlaylist()
  },
  async mounted() {
    await this.getVideosInfo()
  },
  beforeDestroy() {},
  watch: {
    playlists_videos() {
      this.getVideosInfo()
    },
  },
  computed: {
    ...mapState({
      isMobileBreakpoint: (state) => state.user.isMobileBreakpoint,
      playlist: (state) => state.playlist.playlist,
      playlists_videos: (state) => state.playlist.playlists_videos,
    }),
    hasEditVideoPerm() {
      return (
        this.$store.state.user.userPermissions?.includes('put-video') ?? false
      )
    },
    pageTitle() {
      return this.$i18next.t('message.playlist.routeTitle')
    },
    pageSubtitle() {
      return this.playlist?.name ?? ''
    },
    videos() {
      return this.playlist?.videos ?? []
    },
    hasAnyVideo() {
      return Boolean(this.playlists_videos && this.playlists_videos.length > 0)
    },
    playlistDescription() {
      if (!this.playlist.description) return ''
      if (this.expandedDescription) {
        return this.playlist.description
      }
      return `${this.playlist.description?.slice(0, 200)}...`
    },
    playlistTitle() {
      return this.playlist?.name ?? ''
    },
  },
  methods: {
    ...mapMutations('playlist', ['UPDATE_PLAYLIST', 'UPDATE_PLAYLIST_VIDEOS']),
    ...mapMutations('user', ['UPDATE_GLOBAL_SNACKBAR']),
    openAddVideoModal() {
      this.dialogVisible = true
    },
    handleSharePlaylist() {
      this.$root.$emit('openPlaylistShareModal', this.playlist)
    },
    handleBackToPlaylists() {
      this.$router.push({ name: 'Playlists' })
    },
    getLocale() {
      if (this.$i18next.resolvedLanguage === 'en') return 'en-US'
      return 'pt-BR'
    },
    handleSnackbar(param) {
      let text
      let type
      let timeout
      if (param === 'changesSaved') {
        text = i18n.t('message.changesSaved')
        type = 'success'
      }
      if (param === 'maxLength__description') {
        text = i18n.t('message.maximumLengthVideoDesc')
        type = 'error'
        timeout = 12000
      }
      if (param === 'maxLength__title') {
        text = i18n.t('message.maximumLengthVideo')
        type = 'error'
        timeout = 12000
      }
      if (param === 'minLength__title') {
        text = i18n.t('message.minimumLength')
        type = 'error'
        timeout = 12000
      }
      this.UPDATE_GLOBAL_SNACKBAR({
        open: true,
        type: type,
        text: text,
        timeout: timeout ?? 4000,
      })
    },
    async getVideosInfo() {
      if (!window.db) {
        this.$root.$emit('initDexie')
      }
      const videosIds = this.playlists_videos.map((video) => video.video_id)
      let videosInfo = await window.db.videos
        .where('id')
        .anyOf(videosIds)
        .toArray()
      const videosInfoFinal = {}
      videosInfo.forEach((video) => {
        videosInfoFinal[video.id] = video
      })
      this.videosInfo = videosInfoFinal
    },
    async getPlaylist() {
      try {
        const id = this.$route.params.id
        const { data } = await playlistApi.getPlaylist(id)
        const { data: videos } = await playlistApi.getPlaylistVideos(id)
        this.UPDATE_PLAYLIST({ ...data })
        this.UPDATE_PLAYLIST_VIDEOS([...videos])
        this.isLoading = false
      } catch (error) {
        console.log('👀 ~ getPlaylist ~ error:', error)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.f-height {
  height: 100%;
  max-height: 100%;
}

.video {
  &__item {
    display: flex;
    padding: 16px;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid varFigma(neutral-95-lightest);
  }

  &__thumb {
    min-width: 114px;
    max-width: 114px;
    height: 64px;
    border-radius: 4px;
    background-color: black;

    img {
      border-radius: 4px;
    }
  }
}

.card {
  $head-height: 120px;
  &__empty-state {
    &--content {
      border-radius: 8px;
      border: 1px solid varFigma(neutral-95-lightest);
      background-color: varFigma(neutral-95-lightest);
      border-color: varFigma(neutral-95-lightest) !important;
      border-start-end-radius: 0;
      border-start-start-radius: 0;
      min-height: 518px;
      height: calc(100% - #{$head-height});
      display: flex;
      justify-content: center;
      align-items: center;
      flex-direction: column;
    }
    &--head {
      border-radius: 8px;
      border: 1px solid varFigma(neutral-95-lightest);
      border-color: varFigma(neutral-95-lightest) !important;
      border-end-end-radius: 0;
      border-end-start-radius: 0;
    }
  }

  &__video-list {
    border-radius: 8px;
    border: 1px solid varFigma(neutral-95-lightest);
    border-color: varFigma(neutral-95-lightest) !important;
    border-start-end-radius: 0;
    border-start-start-radius: 0;
    min-height: 518px;
    overflow-y: auto;
    height: calc(100% - #{$head-height});
    max-height: calc(100% - #{$head-height});
  }
}

.grid {
  display: grid;
  grid-template-areas: 'settings' 'footer';
  grid-template-columns: 1fr;
  grid-template-rows: 1fr auto;
  column-gap: 0;
  row-gap: 0;
  box-shadow: none !important;
  overflow: hidden;
  height: 100%;

  @media (min-width: 1200px) {
    grid-template-areas:
      'playlists settings'
      'playlists spacer'
      'playlists footer';
    grid-template-columns: 50% auto;
    grid-template-rows: 1fr auto;
  }

  &__playlists {
    grid-area: playlists;
    padding: 24px;
    overflow-y: auto;
    border-left: 1px solid varFigma(neutral-95-lightest);
    border-right: 1px solid varFigma(neutral-95-lightest);
  }

  &__settings {
    grid-area: settings;
    padding: 24px;
    overflow-y: auto;
    overflow-x: hidden;
    margin-right: 2px;

    &--mobile {
      width: 100%;
      max-width: 100%;
      padding: 16px;
      border-left: none;
      border-right: none;

      // Adjust tabs for mobile
      :deep(.v-tabs) {
        margin: 0 -16px;

        .v-tab {
          min-width: auto;
          padding: 0 8px;
        }
      }

      // Adjust content padding
      :deep(.v-tab-item) {
        padding: 16px 0;
      }
    }

    &--no-footer {
      padding-bottom: 80px;
    }
  }

  &__footer {
    grid-area: footer;
    background: varFigma(neutral-100-white);
    border-top: 1px solid varFigma(neutral-95-lightest);
    padding: 8px;

    @media (min-width: 1200px) {
      :deep(.actions-row) {
        margin-right: 100px;
      }
    }
  }
}

.details-font-color {
  color: varFigma(neutral-40-light);
}

.body-label-xs {
  color: varFigma(neutral-40-light);
  font-family: Rubik;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 100%;
}

.mobile-actions {
  gap: 8px;

  .action-btn {
    padding: 6px 12px !important;
    min-width: auto !important;

    ::v-deep .v-btn__content {
      font-size: 12px;
      white-space: nowrap;
    }
  }

  .action-icon {
    font-size: 20px;
  }
}

.action-btn {
  margin-left: 0 !important;
}
</style>

<style lang="scss">
.save-action-divider {
  width: 100%;
  height: 1px;
  background-color: varFigma(neutral-95-lightest);
}
</style>
